import { useState } from "react";
import { useLocation } from "react-router";
import { useLazyQuery, gql } from "@apollo/client";
import { SectionTitle, CmpTable, Loader } from "../../shared";
import SubComponent from "./SubComponent";
import { GET_ALL_BASIC_LOGIN_USER, GET_AUDIT_REPORT, GET_BASIC_LOGIN_EXCEL_USERS } from "./query";
import { toast } from "react-toastify";
import { Helper, exportToExcel } from "../../../utils";

const BasicLoginUserList = () => {
    const backUrl = useLocation().pathname.includes("/user-list") ? "/user-list" : "";
    const [filter, setFilter] = useState(undefined);

    const [basicLoginUserActivity, { loading: auditLoading }] = useLazyQuery(gql`
        ${GET_AUDIT_REPORT}
    `);

    const [getBasicLoginExcelData, { loading: excelLoading }] = useLazyQuery(gql`
        ${GET_BASIC_LOGIN_EXCEL_USERS}
    `);

    const columns = [
        {
            accessorKey: "firstName",
            header: "First Name",
        },
        {
            accessorKey: "lastName",
            header: "Last Name",
        },
        {
            accessorKey: "email",
            header: "Email",
        },
        {
            accessorKey: "username",
            header: "Username",
        },
        {
            accessorKey: "status",
            header: "Status",
            cell: (cell) => (cell.getValue() === 1 ? "Active" : "Inactive"),
        },
    ];

    const onFilterChange = (filter) => {
        setFilter(Object.keys(filter).length > 0 ? filter : undefined);
    };

    const renderSubComponent = (data) => {
        return <SubComponent data={data.row.original} />;
    };

    const getAuditReport = async () => {
        try {
            const { data, error } = await basicLoginUserActivity();

            if (error) {
                toast.error(error.message);
                return;
            }

            const auditTrailData = data.allBasicLoginUserActivities.nodes.map((item) => {
                const {
                    createdAt,
                    creatorUserEmail,
                    action,
                    afectedUser,
                    section,
                    originalValue,
                    newValue,
                } = item;

                const formatDate = (date) => (date ? new Date(date).toLocaleString() : "");
                const formatData = (value) => {
                    if (!isNaN(Number(value))) {
                        return Number(value) === 1 ? "Enabled" : "Disabled";
                    }
                    return value || "";
                };

                return {
                    Date: formatDate(createdAt),
                    User: creatorUserEmail,
                    Action: action,
                    "Affected User": afectedUser,
                    Section: section,
                    "Old Data": formatData(originalValue),
                    "New Data": formatData(newValue),
                };
            });

            exportToExcel(auditTrailData, "Audit Trail Report");
        } catch (error) {
            toast.error("Error fetching or processing audit report");
        }
    };

    const excelExportAll = async (filter) => {
        try {
            const { data } = await getBasicLoginExcelData({
                variables: {
                    country: [],
                    appName: [],
                    filter: filter || undefined,
                },
            });

            const auditTrailData = data.getBasicLoginUser.nodes.map((item) => {
                const {
                    firstName,
                    lastName,
                    email,
                    username,
                    basicLoginUserPermissions,
                    domainName,
                    userByUserId,
                    status,
                    lastLogin,
                    createdAt,
                    loginCount,
                    phone,
                } = item;

                const formatDate = (date) => (date ? new Date(date).toLocaleString() : "");

                const formatData = (value) => {
                    if (!isNaN(Number(value))) {
                        return Number(value) === 1 ? "Enabled" : "Disabled";
                    }
                    return value || "";
                };

                const formatArray = (array) => {
                    return Array.isArray(array) && array.length > 0 ? array.join(", ") : "";
                };

                return {
                    "First Name": firstName,
                    "Last Name": lastName,
                    Email: email,
                    "User Name": username,
                    Profile: "Basic Login User",
                    "System Access":
                        formatArray(
                            basicLoginUserPermissions &&
                                basicLoginUserPermissions.length > 0 &&
                                basicLoginUserPermissions.map((item) => Helper.getAppNameById(item))
                        ) || "",
                    "Domain access": formatArray(domainName),
                    "Created By": `${userByUserId.firstName} ${userByUserId.lastName}`,
                    Status: formatData(status),
                    "Last Login": formatDate(lastLogin),
                    "Created Date": formatDate(createdAt),
                    "Login Count": loginCount || 0,
                    Phone: phone,
                };
            });

            exportToExcel(auditTrailData, "Basic Login User Report (Filter)");
        } catch (error) {
            toast.error(error.message);
        }
    };

    return (
        <>
            {(auditLoading || excelLoading) && <Loader />}
            <SectionTitle
                title="Basic Login Users"
                buttonUrl={`${backUrl}/basic-login/create`}
                backUrl={backUrl || "/"}
            />

            <div className="d-flex mb-5">
                <div className="dropdown mr-3">
                    <button
                        className="btn btn-outline-primary mb-0 dropdown-toggle"
                        type="button"
                        id="dropdownMenuButton"
                        data-toggle="dropdown"
                        aria-haspopup="true"
                        aria-expanded="false"
                    >
                        Download Data
                    </button>
                    <div className="dropdown-menu" aria-labelledby="dropdownMenuButton">
                        {filter && (
                            <button
                                type="button"
                                onClick={() => excelExportAll(filter)}
                                className="piap-btn btn btn-outline-primary mb-0 dropdown-item"
                            >
                                Download Filtered Data
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={() => excelExportAll()}
                            className="piap-btn btn btn-outline-primary mb-0 dropdown-item"
                        >
                            Download All Data
                        </button>
                    </div>
                </div>

                <button type="button" className="btn btn-outline-primary" onClick={getAuditReport}>
                    Audit Trail Report
                </button>
            </div>

            <CmpTable
                QUERY={GET_ALL_BASIC_LOGIN_USER}
                getRowCanExpand={() => true}
                headers={columns}
                renderSubComponent={renderSubComponent}
                dataPath="getBasicLoginUser"
                variables={{ country: [], appName: [] }}
                onFilterChange={onFilterChange}
            />
        </>
    );
};

export default BasicLoginUserList;
